import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Mail, CheckCircle } from 'lucide-react';
import { useAuth, publicApi, formatApiError } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTurnstile } from '../components/Turnstile';
import toast from 'react-hot-toast';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { t } = useLanguage();
  const { login } = useAuth();
  const { getToken } = useTurnstile();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const ttoken = await getToken();
      await login(email, password, ttoken ? { 'X-Turnstile-Token': ttoken } : {});
      toast.success(t('auth.loginSuccess'));
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError('');
    try {
      const ttoken = await getToken();
      await publicApi.post('/auth/forgot-password', { email: forgotEmail }, {
        headers: ttoken ? { 'X-Turnstile-Token': ttoken } : {}
      });
      setForgotSent(true);
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response.data.detail);
      } else if (err.response?.status === 400) {
        setError(formatApiError(err));
      } else {
        setForgotSent(true);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  // Forgot password view
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] px-6 py-16" data-testid="forgot-password-page">
        <div className="max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00aaff] text-sm mb-8 transition-colors">
            <ArrowLeft size={16} /> {t('auth.backToHome')}
          </Link>

          <div className="bg-white rounded-3xl border border-gray-100 p-8 md:p-12 shadow-sm">
            {forgotSent ? (
              <div className="text-center" data-testid="forgot-password-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h1 className="text-2xl font-black text-gray-900 mb-3">{t('auth.forgotPasswordTitle')}</h1>
                <p className="text-gray-500 mb-8 leading-relaxed">
                  {t('auth.forgotPasswordSuccess')}
                </p>
                <button
                  onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); setError(''); }}
                  data-testid="back-to-login-btn"
                  className="w-full bg-[#00aaff] text-white py-3.5 rounded-full font-bold text-sm hover:bg-[#0088dd] transition-colors"
                >
                  {t('auth.forgotPasswordBack')}
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail size={28} className="text-[#00aaff]" />
                  </div>
                  <h1 className="text-2xl font-black text-gray-900 mb-2">{t('auth.forgotPasswordTitle')}</h1>
                  <p className="text-gray-500 text-sm">{t('auth.forgotPasswordDesc')}</p>
                </div>

                {error && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleForgotPassword} className="space-y-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">{t('auth.email')}</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      required
                      placeholder="votre@email.com"
                      data-testid="forgot-email-input"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    data-testid="forgot-submit-btn"
                    className="w-full bg-[#00aaff] text-white py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0088dd] transition-colors disabled:opacity-60"
                  >
                    {forgotLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('auth.forgotPasswordButton')}
                  </button>
                </form>

                <button
                  onClick={() => { setShowForgotPassword(false); setError(''); }}
                  data-testid="back-to-login-link"
                  className="w-full text-center text-sm text-gray-500 mt-6 hover:text-[#00aaff] transition-colors cursor-pointer"
                >
                  {t('auth.forgotPasswordBack')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Login view
  return (
    <div className="min-h-screen bg-[#f5f7fa] px-6 py-16" data-testid="login-page">
      <div className="max-w-md mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00aaff] text-sm mb-8 transition-colors">
          <ArrowLeft size={16} /> {t('auth.backToHome')}
        </Link>

        <div className="bg-white rounded-3xl border border-gray-100 p-8 md:p-12 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-2">{t('auth.loginTitle')}</h1>
            <p className="text-gray-500">{t('auth.loginDesc')}</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">{t('auth.email')}</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="votre@email.com" data-testid="email-input" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('auth.password')}</label>
                <button
                  type="button"
                  onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setError(''); }}
                  data-testid="forgot-password-link"
                  className="text-xs text-[#00aaff] hover:underline font-medium"
                >
                  {t('auth.forgotPassword')}
                </button>
              </div>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required placeholder="••••••••" data-testid="password-input" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors pr-10" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-[#00aaff] text-white py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0088dd] transition-colors disabled:opacity-60">
              {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('auth.loginButton')}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-[#00aaff] hover:underline font-semibold">{t('auth.signUp')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
